import React, { ComponentProps, ReactElement } from 'react';

import PropTypes from 'prop-types';
import { useDropzone, DropzoneOptions, Accept } from 'react-dropzone';

import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { DROPZONE_SIZES, Size, sizePropType } from './consts';
import DefaultActiveDragContent from './layout/DefaultActiveDragContent';
import DefaultContent from './layout/DefaultContent';
import DefaultContentSmall from './layout/DefaultContentSmall';

const defaultRenderDragActiveContent = (
  props: ComponentProps<typeof DefaultActiveDragContent>,
) => <DefaultActiveDragContent {...props} />;

type DropzoneProps = Omit<StylableComponent<HTMLDivElement>, 'onDrop'> &
  DropzoneOptions & {
    disabled?: boolean;
    size?: Size;
    renderContent?: (open: () => void) => ReactElement;
    renderDragActiveContent?: (
      props: ComponentProps<typeof DefaultActiveDragContent>,
    ) => ReactElement;
  };

/**
 * Dropzone
 * A Dropzone is a component that provides an easy way to upload files by dragging
 * and dropping or by selecting the file’s location.
 * It supports multiple file uploads.
 */
const Dropzone = React.forwardRef<HTMLDivElement, DropzoneProps>(
  (
    {
      size = DROPZONE_SIZES.DEFAULT,
      style,
      disabled,
      className,
      renderContent,
      renderDragActiveContent = defaultRenderDragActiveContent,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const dropzoneTokens = theme.tokens?.component?.dropzone;
    const containerTokens = dropzoneTokens?.container;
    const overlayTokens = dropzoneTokens?.overlay;
    const isSmall = size === DROPZONE_SIZES.SMALL;
    const disabledKey = (disabled ? 'true' : 'false') as 'true' | 'false';

    const { isDragActive, getRootProps, getInputProps, open } = useDropzone({
      disabled,
      ...props,
    });

    const dragActiveKey = (isDragActive ? 'true' : 'false') as 'true' | 'false';

    return (
      <div
        ref={ref}
        className={className}
        role="button"
        data-testid="dropzone"
        css={[
          containerTokens?._,
          containerTokens?.size?.[isSmall ? 'small' : 'default'],
          containerTokens?.dragActive?.[dragActiveKey],
          !disabled && !isDragActive && containerTokens?.idle,
          containerTokens?.disabled?.[disabledKey],
          style,
        ]}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <div
          css={[overlayTokens?._, overlayTokens?.dragActive?.[dragActiveKey]]}
        >
          {isDragActive &&
            renderDragActiveContent &&
            renderDragActiveContent({ size })}
        </div>
        {renderContent && renderContent(open)}
        {!renderContent && size === DROPZONE_SIZES.SMALL && (
          <DefaultContentSmall disabled={disabled} open={open} />
        )}
        {!renderContent && size === DROPZONE_SIZES.DEFAULT && (
          <DefaultContent disabled={disabled} open={open} />
        )}
      </div>
    );
  },
);

Dropzone.displayName = 'Dropzone';

Dropzone.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Whether the dropzone is disabled */
  disabled: PropTypes.bool,
  /** Dropzone size */
  size: sizePropType,
  /** Callback to execute on file drop */
  onDrop: PropTypes.func,
  /** File types to accept */
  accept: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.string),
  ) as PropTypes.Validator<Accept>,
  /** Content to render */
  renderContent: PropTypes.func,
  /** Content to render when drag is active */
  renderDragActiveContent: PropTypes.func,
};

export default Object.assign(Dropzone, {
  SIZES: DROPZONE_SIZES,
  Content: DefaultContent,
  ContentSmall: DefaultContentSmall,
});
